import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Eye, EyeOff, Shield, MessageCircle,
  CheckCircle2, AlertCircle, KeyRound, Phone,
} from "lucide-react";
import { API, formatApiError } from "../contexts/AuthContext";
import "../lms.css";

/**
 * ForgotPasswordPage — two-mode UX that matches the product's support model:
 *   Mode A: I remember my current password → enter current + new, we switch it.
 *   Mode B: I don't remember → open WhatsApp to LW help desk with a pre-filled
 *           account-recovery template.
 * Backend: POST /api/auth/self-reset-password — verifies current pw via bcrypt,
 * shares rate-limiting with the login endpoint.
 */
const HELPDESK_NUMBER = "923148799128"; // +92 314 8799128 (no '+' or dashes for wa.me)

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("reset"); // 'reset' | 'help'

  return (
    <div className="lms-root min-h-screen flex flex-col">
      <div aria-hidden className="fixed inset-0 lms-grain" />

      {/* Top bar */}
      <div className="relative z-10 px-6 md:px-10 pt-6">
        <Link
          to="/login"
          className="lms-btn lms-btn-ghost h-9 px-3 text-xs inline-flex items-center gap-1"
          data-testid="forgot-back"
        >
          <ArrowLeft size={12} /> Back to sign in
        </Link>
      </div>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[520px] lms-glass p-7 md:p-10 rounded-[28px] relative"
          style={{ border: "1px solid rgba(92, 238, 255, 0.12)" }}
        >
          <div className="lms-kicker mb-3">— Account recovery</div>
          <h1 className="lms-display text-3xl md:text-4xl text-white leading-tight mb-2">
            Can't <em className="lms-italic-accent">sign in?</em>
          </h1>
          <p className="text-white/60 text-sm mb-6">
            Reset your password with your current one — or ping our help desk and we'll sort it within hours.
          </p>

          {/* Mode tabs */}
          <div className="flex gap-1 mb-5 p-1 rounded-[12px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <ModeTab active={mode === "reset"} onClick={() => setMode("reset")} icon={KeyRound} label="I know my password" testid="forgot-mode-reset" />
            <ModeTab active={mode === "help"} onClick={() => setMode("help")} icon={MessageCircle} label="I forgot completely" testid="forgot-mode-help" />
          </div>

          <AnimatePresence mode="wait">
            {mode === "reset" ? (
              <motion.div
                key="reset"
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.22 }}
              >
                <SelfResetForm onDone={() => navigate("/login", { replace: true })} />
              </motion.div>
            ) : (
              <motion.div
                key="help"
                initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.22 }}
              >
                <HelpDeskPanel />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>
    </div>
  );
}

/* ─── Mode tab ─── */
function ModeTab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex-1 py-2.5 px-3 rounded-[9px] text-xs font-semibold transition flex items-center justify-center gap-2"
      style={{
        background: active ? "rgba(92, 238, 255, 0.16)" : "transparent",
        color: active ? "#5ceeff" : "rgba(255,255,255,0.55)",
        border: active ? "1px solid rgba(92, 238, 255, 0.35)" : "1px solid transparent",
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

/* ─── Self-reset form (Mode A) ─── */
function SelfResetForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const strength = pwStrength(pw);
  const strengthLabel = ["Too weak", "Weak", "Fair", "Strong", "Excellent"][strength];
  const strengthColor = ["#ff6b6b", "#ff6b6b", "#f59e0b", "#5ceeff", "#5ceeff"][strength];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) return setErr("Enter your account email.");
    if (!cur) return setErr("Enter your current password.");
    if (pw.length < 8) return setErr("New password must be at least 8 characters.");
    if (strength < 2) return setErr("Pick a stronger password — mix letters, numbers and a symbol.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    if (cur === pw) return setErr("New password must be different from current.");

    setBusy(true);
    try {
      await axios.post(`${API}/auth/self-reset-password`, {
        email: email.trim(), current_password: cur, new_password: pw,
      });
      setDone(true);
      setTimeout(() => onDone?.(), 2200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="p-5 rounded-[14px] text-center" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
        <CheckCircle2 size={30} className="mx-auto mb-3" style={{ color: "#22c55e" }} />
        <div className="text-lg font-bold text-white mb-1">Password updated</div>
        <div className="text-xs text-white/60">Redirecting you to sign in…</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="forgot-reset-form">
      <DarkInput
        label="Account email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        testid="forgot-reset-email"
        autoFocus
      />
      <DarkInput
        label="Current password"
        type={showCur ? "text" : "password"}
        value={cur}
        onChange={setCur}
        placeholder="••••••••"
        rightAdornment={
          <button type="button" onClick={() => setShowCur(v => !v)} className="text-white/50 hover:text-white" tabIndex={-1} aria-label="toggle visibility">
            {showCur ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        }
        testid="forgot-reset-current"
      />
      <DarkInput
        label="New password"
        type={showNew ? "text" : "password"}
        value={pw}
        onChange={setPw}
        placeholder="at least 8 characters"
        rightAdornment={
          <button type="button" onClick={() => setShowNew(v => !v)} className="text-white/50 hover:text-white" tabIndex={-1} aria-label="toggle visibility">
            {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        }
        testid="forgot-reset-new"
      />
      {pw && (
        <div className="flex items-center gap-2 -mt-2 px-1" data-testid="forgot-reset-strength">
          <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div style={{ width: `${(strength + 1) * 20}%`, height: "100%", background: strengthColor, transition: "width 200ms" }} />
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: strengthColor }}>{strengthLabel}</div>
        </div>
      )}
      <DarkInput
        label="Confirm new password"
        type={showNew ? "text" : "password"}
        value={pw2}
        onChange={setPw2}
        placeholder="re-enter new password"
        testid="forgot-reset-confirm"
      />

      {err && (
        <div className="p-2.5 rounded-[10px] border text-xs flex items-start gap-2"
          style={{ borderColor: "rgba(255,107,107,0.45)", background: "rgba(255,107,107,0.08)", color: "#ff8a8a" }}
          data-testid="forgot-reset-error">
          <AlertCircle size={13} className="shrink-0 mt-0.5" /><div>{err}</div>
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        className="lms-btn lms-btn-primary w-full h-11 text-sm mt-2"
        data-testid="forgot-reset-submit"
      >
        {busy ? "Updating…" : <><Shield size={14} /> Update password <ArrowRight size={13} /></>}
      </button>
    </form>
  );
}

/* ─── Help-desk panel (Mode B) ─── */
function HelpDeskPanel() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [batch, setBatch] = useState("");
  const [lastLogin, setLastLogin] = useState("");

  const message = buildHelpMessage({ name, email, phone, batch, lastLogin });
  const canSend = email.trim() || name.trim();
  const whatsappLink = `https://wa.me/${HELPDESK_NUMBER}?text=${encodeURIComponent(message)}`;

  return (
    <div>
      <div className="mb-4 p-3 rounded-[12px] flex items-start gap-2.5" style={{ background: "rgba(92,238,255,0.06)", border: "1px solid rgba(92,238,255,0.2)" }}>
        <Phone size={14} className="shrink-0 mt-0.5" style={{ color: "#5ceeff" }} />
        <div className="text-xs text-white/80">
          <strong className="text-white">LogicsWallah help desk</strong> · <span style={{ color: "#5ceeff", fontFamily: "monospace" }}>+92 314 8799128</span>
          <div className="mt-0.5 text-white/55">Fill these details so we can identify your account and reset within hours.</div>
        </div>
      </div>

      <div className="space-y-3">
        <DarkInput label="Your full name" value={name} onChange={setName} placeholder="e.g., Hassaan Ali" testid="help-name" />
        <DarkInput label="Account email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" testid="help-email" />
        <DarkInput label="Phone used to register" value={phone} onChange={setPhone} placeholder="03XX-XXXXXXX" testid="help-phone" />
        <DarkInput label="Batch (if known)" value={batch} onChange={setBatch} placeholder="e.g., MDCAT 2026 Freshers" testid="help-batch" />
        <DarkInput label="When did you last log in? (optional)" value={lastLogin} onChange={setLastLogin} placeholder="e.g., 2 weeks ago" testid="help-lastlogin" />
      </div>

      {/* Live message preview */}
      <div className="mt-4 p-3 rounded-[10px] text-[11px] whitespace-pre-wrap leading-relaxed"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)", fontFamily: "monospace" }}
        data-testid="help-msg-preview">
        {message}
      </div>

      <a
        href={canSend ? whatsappLink : undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { if (!canSend) e.preventDefault(); }}
        className="lms-btn w-full h-11 text-sm mt-4 inline-flex items-center justify-center gap-2 font-semibold"
        style={{
          background: canSend ? "#25D366" : "rgba(255,255,255,0.08)",
          borderColor: canSend ? "#25D366" : "rgba(255,255,255,0.1)",
          color: canSend ? "white" : "rgba(255,255,255,0.35)",
          opacity: canSend ? 1 : 0.6, cursor: canSend ? "pointer" : "not-allowed",
        }}
        data-testid="help-send-whatsapp"
      >
        <MessageCircle size={14} /> Send via WhatsApp
      </a>
      {!canSend && (
        <div className="mt-2 text-[11px] text-center" style={{ color: "rgba(255,255,255,0.4)" }}>
          Enter at least your name or email so we can find you.
        </div>
      )}
    </div>
  );
}

/* ─── Reusable dark input ─── */
function DarkInput({ label, type = "text", value, onChange, placeholder, rightAdornment, testid, autoFocus }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-white/50 mb-1.5">{label}</div>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="w-full h-11 px-3.5 rounded-[10px] text-sm text-white outline-none transition"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          onFocus={(e) => { e.target.style.borderColor = "rgba(92,238,255,0.45)"; e.target.style.background = "rgba(92,238,255,0.04)"; }}
          onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.background = "rgba(255,255,255,0.04)"; }}
          data-testid={testid}
        />
        {rightAdornment && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightAdornment}
          </div>
        )}
      </div>
    </label>
  );
}

function pwStrength(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

function buildHelpMessage({ name, email, phone, batch, lastLogin }) {
  return `Assalam-o-Alaikum LogicsWallah team! 👋

I can't access my account and I don't remember my current password. Please help me reset it.

*My details:*
• Full name: ${name || "(not provided)"}
• Account email: ${email || "(not provided)"}
• Registered phone: ${phone || "(not provided)"}
• Batch: ${batch || "(not provided)"}
• Last login: ${lastLogin || "(not provided)"}

Request: kindly verify my identity and help me regain access to my Logician dashboard.

JazakAllah khair.`;
}
